import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SharedModule } from 'src/app/layouts/full/shared/shared.module';
import { HeelCup, KirbySkive, FirstRayCutout, Flanges, ArchModification, ArchReinforcement, MortonsExtension, Cutout, MetatarsalPad, FascialGroove, Styloid, NavicularDepression, DensityReduction, ForefootPosting, RearfootPosting, HeelLift } from 'src/app/models/formResponse.model';

export interface radioButtonAndOther {
  title: string,
  selectedName?: string,
  selectedValue?: {
    left: number,
    right: number
  },
  options: {
    name: string;
    value?: number;
  }[],
}

export type footSelection = "left" | "right" | "both";

export type LeftRightBothTypes = "left" | "right" | "both";

export class ValueNumberFormGroup extends FormGroup {
  constructor(initialValue?: any) {
    super({
      value: new FormControl(initialValue?.value, Validators.required),
    });
  }
}

interface LateralMedialNumberProps {
  lateral: {
    value: number;
  };
  medial: {
    value: number;
  };
}

@Component({
  selector: 'app-insole-component',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './insole-component.component.html',
  styleUrl: './insole-component.component.scss'
})
export class InsoleComponentComponent {

  @Output() updateEvent = new EventEmitter<any>(); // Envía eventos al padre

  @Input() componentForm: FormGroup;
  @Input() imageUrl: string;
  @Input() title: string;
  @Input() translationKey: string;  // Para las traducciones
  @Input() medialLateralType: "unique" | "multiple" = null;


  @Input() selectOptions: any[];
  @Input() reversedBooleanOption: boolean;
  @Input() typeSelectOptions: any[];

  @Input() public foot: string = null;
  public hasOptionsToFill: boolean = false;

  @Input() public footSelectionOptions: { value: string, title: string, translationKey: string, disabled: boolean }[] = []
  @Input() expandAll: boolean = false
  @Input() disabledReason: string = null;
  @Input() warningMessage: string = null;

  isExpanded: boolean = false
  originalSubtitles: { subtitleLeft: string, subtitleRight: string };
  hideSingleSelectionIndicator = true;

  ngOnInit(): void {
    // Emitir el formulario inicial al inicializar
    this.updateEvent.emit(this.componentForm);

    // Escuchar cambios en el formulario
    this.componentForm.valueChanges.subscribe(value => {
      this.updateEvent.emit(this.componentForm); // Emitir el nuevo estado del formulario
    });

    // Listen to change of foot selection
    this.componentForm.get('footSelection')!.valueChanges.subscribe(value => {
      this.disableLeftOrRightOnFootSelectionChanged(value);
    });

    // Check if has options to select or just "left" "right" will be enought
    this.optionsAvaialable();

    // Initialize this.foot with the current value of footSelection
    const footSelection = this.componentForm.get('footSelection')?.value;
    if (footSelection) {
      this.foot = footSelection;
    }

    // Guardar los valores originales de los subtitulos cuando se carga el formulario
    this.originalSubtitles = this.getHeaderSubtitle(this.componentForm.value);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['expandAll'] && changes['expandAll'].currentValue !== undefined) {
      this.isExpanded = this.expandAll; // Actualiza el estado del accordion
    }
  }

  changeFootSelected(newFoot: string, event: MouseEvent) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const footControl = this.componentForm.get('footSelection');
    // Use this.foot to check previous state because formControl value might be dirty from Material click
    const currentFoot = this.foot;

    // Si el pie ya está seleccionado, lo deseleccionamos
    if (currentFoot == newFoot) {
      footControl.setValue(null);
      this.foot = null;
    } else {
      footControl.setValue(newFoot); // Guardar el valor seleccionado
      this.foot = newFoot
    }
  }

  onMedialChanged(value: number): void {
    this.componentForm.get('left.medial.value')?.setValue(value);
    this.componentForm.get('right.medial.value')?.setValue(value);
  }

  onSelectionChange(foot: footSelection, event): void {
    this.componentForm.get(`${foot}.value`)?.setValue(event.value == "others" ? 0 : event.value);
  }

  onChangeLateralMedialValue(foot: footSelection, type: "medial" | "lateral", event): void {
    this.componentForm.get(`${foot}.${type}.value`)?.setValue(event.value == "others" ? 0 : event.value);
  }

  onChangeLateralMedialWidthHeightValue(foot: footSelection, type: "medial" | "lateral", dimension: "width" | "height", event): void {
    this.componentForm.get(`${foot}.${type}.${dimension}.value`)?.setValue(event.value == "others" ? 0 : event.value);
  }

  // It's used to change one foot only medial or lateral, cannot contain both
  public onChangeLateralMedialPositionValue(foot: footSelection, type: "medial" | "lateral"): void {

    // Disable lateral when they select medial
    if (type === "medial") {
      this.componentForm.get(`${foot}.medial`)!.enable();
      this.componentForm.get(`${foot}.lateral`)!.disable();
    }

    // Disable medial when they select lateral
    if (type === "lateral") {
      this.componentForm.get(`${foot}.lateral`)!.enable();
      this.componentForm.get(`${foot}.medial`)!.disable();

    }
  }

  toggleSelectedRadioAndOtherOption(radioAndOther: radioButtonAndOther, newSelectedName: string, newSelectedValue: number) {

    // Init selectedValue if does not exists
    if (!radioAndOther.selectedValue) {
      radioAndOther.selectedValue = { left: null, right: null };
    }

    // Change the value on left foot
    if (this.foot == "left" || this.foot == "both") {
      radioAndOther.selectedValue.left = newSelectedValue === radioAndOther.selectedValue.left ? null : newSelectedValue;
    }

    // Change the value on right foot
    if (this.foot == "right" || this.foot == "both") {
      radioAndOther.selectedValue.right = newSelectedValue === radioAndOther.selectedValue.right ? null : newSelectedValue;
    }

    // Change the value on left foot
    if (!this.foot) {
      radioAndOther.selectedValue = { left: null, right: null };
    }

    // Show and hide the input fields to set (left and right of different options)
    // when it's others it shows the input to choose another number
    radioAndOther.selectedName = newSelectedName === radioAndOther.selectedName ? null : newSelectedName;

  }


  // It's used to show details about the component
  optionsAvaialable() {
    if (this.selectOptions || this.reversedBooleanOption || this.componentForm.get('left.position.heel')) {
      this.hasOptionsToFill = true;
    }
  }

  // Enable and disable the right / right foot depending on what's selected
  private disableLeftOrRightOnFootSelectionChanged(value: LeftRightBothTypes): void {

    if (value === 'left' || value === 'both') {
      this.componentForm.get('left')!.enable();

      // Disable lateral or medial, because just one can be active when it's "unique"
      if (this.medialLateralType === "unique") {
        this.onChangeLateralMedialPositionValue('left', this.componentForm.get('left.position').value)
      }
    } else {
      this.componentForm.get('left')!.disable();
    }

    if (value === 'right' || value === 'both') {
      this.componentForm.get('right')!.enable();

      // Disable lateral or medial, because just one can be active when it's "unique"
      if (this.medialLateralType === "unique") {
        console.log("Has medial lateral R");
        this.onChangeLateralMedialPositionValue('right', this.componentForm.get('right.position').value)
      }
    } else {
      this.componentForm.get('right')!.disable();
    }

  }

  formatValue(value: any): string {
    return typeof value === 'number' ? value + 'mm' : '';
  }

  capitalizeFirstLetter(string) {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  getHeaderSubtitle(componentTitle: HeelCup | KirbySkive | FirstRayCutout | Flanges | ArchModification | ArchReinforcement | MortonsExtension |
    Cutout | MetatarsalPad | FascialGroove | Styloid | NavicularDepression | DensityReduction | ForefootPosting | RearfootPosting | HeelLift): { subtitleLeft: string, subtitleRight: string } {

    let subtitleLeft = ""
    let subtitleRight = ""

    let leftOrBoth = ["left", "both"];
    let rightOrBoth = ["right", "both"];

    // Heel Cup
    if (componentTitle.type === "heelCup") {
      // Left foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        subtitleLeft += ` ${componentTitle.left?.value}mm`
      }
      // Right foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        subtitleRight += ` ${componentTitle.right?.value}mm`
      }

      return { subtitleLeft, subtitleRight };
    }



    // Kirby Skive
    if (componentTitle.type === "kirbySkive") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        // Lateral
        if (componentTitle.left?.lateral?.value) {
          subtitleLeft += ` Lateral ${componentTitle.left?.lateral.value}mm`
        }
        // Medial
        if (componentTitle.left?.medial?.value) {
          subtitleLeft += ` Medial ${componentTitle.left?.medial.value}mm`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        // Lateral
        if (componentTitle.right?.lateral?.value) {
          subtitleRight += ` Lateral ${componentTitle.right?.lateral.value}mm`
        }
        // Medial
        if (componentTitle.right?.medial?.value) {
          subtitleRight += ` Medial ${componentTitle.right?.medial.value}mm`
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // First Ray Cutout
    if (componentTitle.type === "firstRayCutout") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.left?.value) {
          subtitleLeft += ` ${componentTitle.left?.value}`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.right?.value) {
          subtitleRight += ` ${componentTitle.right?.value}`
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // Flanges
    if (componentTitle.type === "flanges") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        // Lateral
        if (componentTitle.left?.lateral?.width?.value) {
          subtitleLeft += ` Lateral Width ${componentTitle.left?.lateral.width.value}mm`
        }

        if (componentTitle.left?.lateral?.height?.value) {
          subtitleLeft += ` Lateral Height ${componentTitle.left?.lateral.height.value}mm`
        }

        // Medial
        if (componentTitle.left?.medial?.width?.value) {
          subtitleLeft += ` Medial Width ${componentTitle.left?.medial.width.value}mm`
        }

        if (componentTitle.left?.medial?.height?.value) {
          subtitleLeft += ` Medial Height ${componentTitle.left?.medial.height.value}mm`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        // Lateral
        if (componentTitle.right?.lateral?.width?.value) {
          subtitleRight += ` Lateral Width ${componentTitle.right?.lateral.width.value}mm`
        }

        if (componentTitle.right?.lateral?.height?.value) {
          subtitleRight += ` Lateral Height ${componentTitle.right?.lateral.height.value}mm`
        }

        // Medial
        if (componentTitle.right?.medial?.width.value) {
          subtitleRight += ` Medial Width ${componentTitle.right?.medial.width.value}mm`
        }

        if (componentTitle.right?.medial?.height.value) {
          subtitleRight += ` Medial Height ${componentTitle.right?.medial.height.value}mm`
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // Arch Modification
    if (componentTitle.type === "archModification") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        // Lateral
        if (componentTitle.left?.lateral?.value) {
          subtitleLeft += ` Lateral ${componentTitle.left?.lateral.value}mm`
        }
        if (componentTitle.left?.lateral?.value === 0) {
          subtitleLeft += ` Lateral Per Cast`
        }
        // Medial
        if (componentTitle.left?.medial?.value) {
          subtitleLeft += ` Medial ${componentTitle.left?.medial.value}mm`
        }
        if (componentTitle.left?.medial?.value === 0) {
          subtitleLeft += ` Medial Per Cast`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        // Lateral
        if (componentTitle.right?.lateral?.value) {
          subtitleRight += ` Lateral ${componentTitle.right?.lateral.value}mm`
        }
        if (componentTitle.right?.lateral?.value === 0) {
          subtitleRight += ` Lateral Per Cast`
        }
        // Medial
        if (componentTitle.right?.medial?.value) {
          subtitleRight += ` Medial ${componentTitle.right?.medial.value}mm`
        }
        if (componentTitle.right?.medial?.value === 0) {
          subtitleRight += ` Medial Per Cast`
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // Arch Reinforcement
    if (componentTitle.type === "archReinforcement") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.left?.value) {
          subtitleLeft += ` ${componentTitle.left?.value}mm`
        }

        if (componentTitle.left?.type) {
          subtitleLeft += ` ${componentTitle.left.type}`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.right?.value) {
          subtitleRight += ` ${componentTitle.right?.value}mm`
        }

        if (componentTitle.right?.type) {
          subtitleRight += ` ${componentTitle.right.type}`
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // // Morton's Extension
    if (componentTitle.type === "mortonsExtension") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.left) {
          subtitleLeft += ` `
        }
        if (componentTitle.left?.value) {
          subtitleLeft += ` ${componentTitle.left?.value}mm`
        }
        if (componentTitle.left?.reversed) {
          subtitleLeft += ` Reversed`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.right) {
          subtitleRight += ` `
        }
        if (componentTitle.right?.value) {
          subtitleRight += ` ${componentTitle.right?.value}mm`
        }
        if (componentTitle.right?.reversed) {
          subtitleRight += ` Reversed`
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // // Heel Cutout
    if (componentTitle.type === "cutout") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        subtitleLeft += ` Cutout`;
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        subtitleRight += ` Cutout`;
      }

      return { subtitleLeft, subtitleRight };
    }

    // // Metatarsal pad
    if (componentTitle.type === "metatarsalPad") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        // Drop
        if (componentTitle.left?.type == "drop") {
          subtitleLeft += ` Drop ${componentTitle.left?.value}mm`
        }
        // Full Bar
        if (componentTitle.left?.type == "fullbar") {
          subtitleLeft += ` Fullbar ${componentTitle.left?.value}mm`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        // Drop
        if (componentTitle.right?.type == "drop") {
          subtitleRight += ` Drop ${componentTitle.right?.value}mm`
        }
        // Full Bar
        if (componentTitle.right?.type == "fullbar") {
          subtitleRight += ` Fullbar ${componentTitle.right?.value}mm`
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // // Fascial Groove
    if (componentTitle.type === "fascialGroove") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        subtitleLeft += ` `;
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        subtitleRight += ` `;
      }

      return { subtitleLeft, subtitleRight };
    }

    // // Styloid
    if (componentTitle.type === "styloid") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.left?.value) {
          subtitleLeft += ` ${componentTitle.left?.value}mm`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.right?.value) {
          subtitleRight += ` ${componentTitle.right?.value}mm`
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // // Navicular Depression
    if (componentTitle.type === "navicularDepression") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.left?.value) {
          subtitleLeft += ` ${componentTitle.left?.value}mm`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.right?.value) {
          subtitleRight += ` ${componentTitle.right?.value}mm`
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // Density Reduction
    if (componentTitle.type === "densityReduction") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.left) {
          subtitleLeft += ` `
        }
        if (componentTitle.left?.position.heel) {
          subtitleLeft += ` Heel`
        }
        if (componentTitle.left?.position.lateralArch) {
          subtitleLeft += ` Lateral Arch`
        }
        if (componentTitle.left?.position.medialArch) {
          subtitleLeft += ` Medial Arch`
        }
        if (componentTitle.left?.position.metatarsal) {
          subtitleLeft += ` Metatarsal`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        if (componentTitle.right) {
          subtitleRight += ` `
        }
        if (componentTitle.right?.position.heel) {
          subtitleRight += `Heel`
        }
        if (componentTitle.right?.position.lateralArch) {
          subtitleRight += ` Lateral Arch`
        }
        if (componentTitle.right?.position.medialArch) {
          subtitleRight += ` Medial Arch`
        }
        if (componentTitle.right?.position.metatarsal) {
          subtitleRight += ` Metatarsal`
        }
      }

      return {
        subtitleLeft,
        subtitleRight
      }
    }

    // Forefoot Posting
    if (componentTitle.type === "forefootPosting") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        // Lateral
        if (componentTitle.left?.lateral?.value) {
          subtitleLeft += ` Lateral ${componentTitle.left?.lateral.value}mm`

          // Si tiene balancedToRearfoot
          if (componentTitle.left?.lateral?.balancedToRearfoot) {
            subtitleLeft += ` (Balanced to Rearfoot)`;
          }
        }

        // Medial
        if (componentTitle.left?.medial?.value) {
          subtitleLeft += ` Medial ${componentTitle.left?.medial.value}mm`

          // Si tiene balancedToRearfoot
          if (componentTitle.left?.medial?.balancedToRearfoot) {
            subtitleLeft += ` (Balanced to Rearfoot)`;
          }
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        // Lateral
        if (componentTitle.right?.lateral?.value) {
          subtitleRight += ` Lateral ${componentTitle.right?.lateral.value}mm`

          // Si tiene balancedToRearfoot
          if (componentTitle.right?.lateral?.balancedToRearfoot) {
            subtitleRight += ` (Balanced to Rearfoot)`;
          }
        }
        // Medial
        if (componentTitle.right?.medial?.value) {
          subtitleRight += ` Medial ${componentTitle.right?.medial.value}mm`

          // Si tiene balancedToRearfoot
          if (componentTitle.right?.medial?.balancedToRearfoot) {
            subtitleRight += ` (Balanced to Rearfoot)`;
          }
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // Rearfoot Posting
    if (componentTitle.type === "rearfootPosting") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        // Si tiene type
        if (componentTitle.left?.type == 'uShape') {
          subtitleLeft += ` U Shape`;
        }

        if (componentTitle.left?.type == 'full') {
          subtitleLeft += ` Full`;
        }

        if (componentTitle.left?.type == 'medialHalf') {
          subtitleLeft += ` Medial Half`;
        }

        if (componentTitle.left?.type == 'lateralHalf') {
          subtitleLeft += ` Lateral Half`;
        }

        // Lateral
        if (componentTitle.left?.lateral?.value) {
          subtitleLeft += ` Lateral ${componentTitle.left?.lateral.value}mm`

          // Si tiene extrinsic
          if (componentTitle.left?.lateral?.extrinsic) {
            subtitleLeft += ` (Extrinsic)`;
          }
        }

        // Medial
        if (componentTitle.left?.medial?.value) {
          subtitleLeft += ` Medial ${componentTitle.left?.medial.value}mm`

          // Si tiene extrinsic
          if (componentTitle.left?.medial?.extrinsic) {
            subtitleLeft += ` (Extrinsic)`;
          }
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        // Si tiene type
        if (componentTitle.right?.type == 'uShape') {
          subtitleRight += ` U Shape`;
        }

        if (componentTitle.right?.type == 'full') {
          subtitleRight += ` Full`;
        }

        if (componentTitle.right?.type == 'medialHalf') {
          subtitleRight += ` Medial Half`;
        }

        if (componentTitle.right?.type == 'lateralHalf') {
          subtitleRight += ` Lateral Half`;
        }

        // Lateral
        if (componentTitle.right?.lateral?.value) {
          subtitleRight += ` Lateral ${componentTitle.right?.lateral.value}mm`

          // Si tiene balancedToRearfoot
          if (componentTitle.right?.lateral?.extrinsic) {
            subtitleRight += ` (Extrinsic)`;
          }
        }
        // Medial
        if (componentTitle.right?.medial?.value) {
          subtitleRight += ` Medial ${componentTitle.right?.medial.value}mm`

          // Si tiene balancedToRearfoot
          if (componentTitle.right?.medial?.extrinsic) {
            subtitleRight += ` (Extrinsic)`;
          }
        }
      }

      return { subtitleLeft, subtitleRight };
    }

    // Heel Lift
    if (componentTitle.type === "heelLift") {
      // Left Foot
      if (leftOrBoth.includes(componentTitle.footSelection)) {
        // Si tiene type
        if (componentTitle.left?.type == 'uShape') {
          subtitleLeft += ` U Shape`;
        }

        if (componentTitle.left?.type == 'full') {
          subtitleLeft += ` Full`;
        }

        // Si tiene left value
        if (componentTitle.left?.value) {
          subtitleLeft += ` ${componentTitle.left?.value}mm`
        }
      }

      // Right Foot
      if (rightOrBoth.includes(componentTitle.footSelection)) {
        // Si tiene type
        if (componentTitle.right?.type == 'uShape') {
          subtitleRight += ` U Shape`;
        }

        if (componentTitle.right?.type == 'full') {
          subtitleRight += ` Full`;
        }

        // Si tiene right value
        if (componentTitle.right?.value) {
          subtitleRight += ` ${componentTitle.right?.value}mm`
        }

      }

      return { subtitleLeft, subtitleRight };
    }

    return {
      subtitleLeft,
      subtitleRight
    }
  }

  // Función que detecta si el subtítulo ha cambiado
  isSubtitleModified(current: { subtitleLeft: string, subtitleRight: string }): boolean {
    return this.originalSubtitles.subtitleLeft !== current.subtitleLeft ||
      this.originalSubtitles.subtitleRight !== current.subtitleRight;
  }

  // Función para verificar si cada subtítulo en particular ha cambiado
  isLeftSubtitleModified(): boolean {
    return this.originalSubtitles.subtitleLeft !== this.getHeaderSubtitle(this.componentForm.value).subtitleLeft;
  }

  isRightSubtitleModified(): boolean {
    return this.originalSubtitles.subtitleRight !== this.getHeaderSubtitle(this.componentForm.value).subtitleRight;
  }

  onSubmit(): void {
    // Aquí procesas los datos del formulario. Por ejemplo:
    console.log('Formulario enviado:', this.componentForm.value);
    console.log(this.componentForm.errors); // Muestra los errores del FormGroup
    console.log(this.componentForm.valid)
  }
}
